// ==================== BILLING PAGE ====================

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CreditCard, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  ChevronRight,
  Zap,
  Crown,
  ArrowUpRight,
  ToggleLeft,
  ToggleRight,
  AlertTriangle,
  Bot,
} from 'lucide-react';
import { CouponField } from '../../../components/coupons/CouponField';
import { Button, ConfirmDialog, Modal } from '../../../components/ui';
import {
  subscriptionService,
  type Subscription,
  type PaymentMethod,
  type LeadGenAddonQuote,
} from '../../../services/subscription.service';
import { ROUTES } from '../../../constants/routes.constants';
import { useAuth } from '../../../contexts/auth';

const statusStyles = {
  PENDING: { bg: 'bg-blue-500/15', text: 'text-blue-400', icon: AlertCircle },
  ACTIVE: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', icon: CheckCircle },
  PAST_DUE: { bg: 'bg-yellow-500/15', text: 'text-yellow-400', icon: AlertTriangle },
  CANCELLED: { bg: 'bg-red-500/15', text: 'text-red-400', icon: XCircle },
  EXPIRED: { bg: 'bg-gray-500/15', text: 'text-gray-400', icon: XCircle },
};

const BillingPage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [togglingAutoRenew, setTogglingAutoRenew] = useState(false);
  const [togglingLeadGen, setTogglingLeadGen] = useState(false);
  const [showEnableAddonDialog, setShowEnableAddonDialog] = useState(false);
  const [showDisableAddonDialog, setShowDisableAddonDialog] = useState(false);
  const [showAutoRenewDialog, setShowAutoRenewDialog] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedAddonPmId, setSelectedAddonPmId] = useState<string | null>(null);
  const [addonQuote, setAddonQuote] = useState<LeadGenAddonQuote | null>(null);
  const [addonCouponInput, setAddonCouponInput] = useState('');
  const [addonCouponCode, setAddonCouponCode] = useState('');
  const [addonQuotedCode, setAddonQuotedCode] = useState('');
  const [couponError, setCouponError] = useState<string | null>(null);
  const addonQuoteRequest = useRef(0);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [resumeSuccess, setResumeSuccess] = useState(false);

  const fetchSubscription = useCallback(async () => {
    try {
      setLoading(true);
      const response = await subscriptionService.getCurrentSubscription();
      if (response.success) {
        setSubscription(response.data ?? null);
      } else {
        setError(response.message || 'Failed to load subscription');
      }
    } catch {
      setError('Failed to load subscription details');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPaymentMethods = useCallback(async () => {
    try {
      const response = await subscriptionService.getPaymentMethods();
      if (response.success && response.data) {
        setPaymentMethods(response.data);
      }
    } catch {
      // Non-fatal: the enable dialog will prompt to add a method if none load.
    }
  }, []);

  // Exact add-on pricing for this subscription (prorated charge now + per-cycle renewal).
  const fetchAddonQuote = useCallback(async () => {
    const request = ++addonQuoteRequest.current;
    try {
      const response = await subscriptionService.getLeadGenAddonQuote(addonCouponCode || undefined);
      if (request !== addonQuoteRequest.current) return;
      if (response.success && response.data) {
        setAddonQuote(response.data);
        setAddonQuotedCode(addonCouponCode);
        setCouponError(null);
      } else {
        // Only a typed coupon's rejection is shown; other failures keep the enable button disabled as before.
        setAddonQuote(null);
        setCouponError(addonCouponCode ? response.message : null);
      }
    } catch {
      // Non-fatal: the enable button stays disabled until a quote loads.
    }
  }, [addonCouponCode]);

  useEffect(() => {
    fetchSubscription();
    fetchPaymentMethods();
  }, [fetchSubscription, fetchPaymentMethods]);

  useEffect(() => { void fetchAddonQuote(); }, [fetchAddonQuote]);

  const handleToggleAutoRenew = async () => {
    if (!subscription) return;
    
    try {
      setTogglingAutoRenew(true);
      const response = await subscriptionService.toggleAutoRenew(!subscription.autoRenew);
      if (response.success) {
        setSubscription(prev => prev ? { ...prev, autoRenew: !prev.autoRenew } : null);
      }
    } catch (err) {
      console.error('Failed to toggle auto-renewal:', err);
    } finally {
      setTogglingAutoRenew(false);
      setShowAutoRenewDialog(false);
    }
  };

  // Open the enable dialog with the default payment method pre-selected
  // (falls back to the first saved method).
  const openEnableAddonDialog = () => {
    const preferred =
      paymentMethods.find((pm) => pm.isDefault)?.id ?? paymentMethods[0]?.id ?? null;
    setSelectedAddonPmId(preferred);
    setError(null);
    setShowEnableAddonDialog(true);
  };

  const handleToggleLeadGenAddon = async (enable: boolean) => {
    if (!subscription) return;

    try {
      setTogglingLeadGen(true);
      setError(null);
      const response = await subscriptionService.toggleLeadGenAddon(
        enable,
        enable ? selectedAddonPmId ?? undefined : undefined,
        enable && !addonQuote?.alreadyPaidThisPeriod ? addonCouponCode || undefined : undefined,
      );
      if (response.success) {
        // Refetch the full subscription (keeps payment method / history) and
        // refresh auth so the sidebar shows/hides the Lead Generation section.
        await Promise.all([fetchSubscription(), fetchAddonQuote()]);
        await refreshUser();
      } else {
        setError(response.message || 'Failed to update the Lead Generation add-on');
      }
    } catch (err) {
      console.error('Failed to toggle Lead Generation add-on:', err);
      setError('Failed to update the Lead Generation add-on');
    } finally {
      setTogglingLeadGen(false);
      setShowEnableAddonDialog(false);
      setShowDisableAddonDialog(false);
    }
  };

  // Add-on pricing: billed per cycle with the plan (12 months on yearly), and
  // enabling mid-cycle charges the months left in the current period.
  const addonCycleLabel = subscription?.billingCycle === 'YEARLY' ? 'year' : 'month';
  const addonRecurringPrice = subscription?.leadGenAddonEnabled
    ? Number(subscription.leadGenAddonAmount)
    : addonQuote?.recurringAmount ?? 0;
  const addonChargeNow = addonQuote?.amountDue ?? addonQuote?.chargeNow ?? 0;
  const addonAlreadyPaid = addonQuote?.alreadyPaidThisPeriod ?? false;

  const handleCancelSubscription = async () => {
    try {
      setCancelling(true);
      const response = await subscriptionService.cancelSubscription();
      if (response.success) {
        setCancelSuccess(true);
        setShowCancelDialog(false);
        // Update local state to show cancelled status
        setSubscription(prev => prev ? { ...prev, status: 'CANCELLED' } : null);
        // Redirect after 3 seconds
        setTimeout(() => {
          navigate('/app/dashboard');
        }, 3000);
      } else {
        setError(response.message || 'Failed to cancel subscription');
        setShowCancelDialog(false);
      }
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      setError('Failed to cancel subscription. Please try again.');
      setShowCancelDialog(false);
    } finally {
      setCancelling(false);
    }
  };

  const handleResumeSubscription = async () => {
    try {
      setResuming(true);
      setError(null);
      const response = await subscriptionService.resumeSubscription();
      if (response.success) {
        setResumeSuccess(true);
        // Update local state to show active status
        setSubscription(prev => prev ? { ...prev, status: 'ACTIVE', autoRenew: true } : null);
        // Clear success message after 5 seconds
        setTimeout(() => {
          setResumeSuccess(false);
        }, 5000);
      } else {
        setError(response.message || 'Failed to resume subscription');
      }
    } catch (err) {
      console.error('Failed to resume subscription:', err);
      setError('Failed to resume subscription. Please try again.');
    } finally {
      setResuming(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  };

  const formatDate = (dateString: string, includeTime: boolean = false) => {
    const date = new Date(dateString);
    const dateOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    
    if (includeTime) {
      return date.toLocaleString('en-US', {
        ...dateOptions,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    }
    
    return date.toLocaleDateString('en-US', dateOptions);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="flex flex-col items-center gap-2">
          <RefreshCw className="w-6 h-6 text-orange-500 animate-spin" />
          <p className="text-sm text-(--color-muted-gray)">Loading subscription...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
        <AlertCircle className="w-6 h-6 text-red-400 mx-auto mb-2" />
        <p className="text-sm text-red-400">{error}</p>
        <Button variant="secondary" size="sm" onClick={fetchSubscription} className="mt-3">
          Try Again
        </Button>
      </div>
    );
  }

  if (!subscription) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#121723] rounded-xl border border-gray-800 p-6 text-center"
      >
        <div className="w-12 h-12 rounded-xl bg-linear-to-br from-orange-500/20 to-orange-600/10 flex items-center justify-center mx-auto mb-3">
          <CreditCard className="w-6 h-6 text-orange-400" />
        </div>
        <h3 className="text-base font-heading font-semibold text-white mb-1.5">No Active Subscription</h3>
        <p className="text-sm text-(--color-muted-gray) mb-4 max-w-sm mx-auto">
          Choose a plan that fits your business needs and unlock the full potential of AmplifyMsg.
        </p>
        <Link to="/app/select-plan">
          <Button variant="primary">
            <Zap className="w-3.5 h-3.5 mr-1.5" />
            View Plans
          </Button>
        </Link>
      </motion.div>
    );
  }

  const statusConfig = statusStyles[subscription.status as keyof typeof statusStyles] || statusStyles.PENDING;
  const StatusIcon = statusConfig.icon;

  return (
    <div className="space-y-4">
      {/* Current Plan Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-linear-to-br from-[#121723] to-navy-200 rounded-xl border border-gray-800 overflow-hidden"
      >
        {/* Plan Header */}
        <div className="p-4 border-b border-gray-800">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-linear-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                <Crown className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-heading font-semibold text-white">{subscription.plan.name}</h2>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${statusConfig.bg} ${statusConfig.text}`}>
                    <StatusIcon className="w-3 h-3" />
                    {subscription.status}
                  </span>
                </div>
                <p className="text-xs text-(--color-muted-gray) mt-0.5">
                  {subscription.billingCycle === 'YEARLY' ? 'Annual' : 'Monthly'} billing
                </p>
              </div>
            </div>
            {subscription.status === 'CANCELLED' ? (
              <Button
                variant="primary"
                size="sm"
                onClick={handleResumeSubscription}
                disabled={resuming}
                className="shrink-0"
              >
                {resuming ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    Resuming...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                    Re-subscribe
                  </>
                )}
              </Button>
            ) : (
              <Link 
                to={ROUTES.CUSTOMER.UPGRADE_PLAN}
                className="inline-flex items-center gap-1 text-orange-400 hover:text-orange-300 transition-colors text-xs font-medium"
              >
                Upgrade Plan
                <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
        </div>

        {/* Billing Info */}
        <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-0.5">
            <p className="text-xs text-(--color-muted-gray)">Current Period</p>
            <p className="text-sm text-white font-medium">
              {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
            </p>
          </div>
          <div className="space-y-0.5">
            <p className="text-xs text-(--color-muted-gray)">Next Billing Date</p>
            <p className="text-sm text-white font-medium">
              {subscription.nextBillingDate ? formatDate(subscription.nextBillingDate) : 'N/A'}
            </p>
          </div>
          <div className="space-y-0.5">
            <p className="text-xs text-(--color-muted-gray)">Recurring Amount</p>
            <p className="text-white font-semibold">
              {formatCurrency(subscription.nextRenewalAmount ?? subscription.recurringAmount, subscription.currency)}
              <span className="text-(--color-muted-gray) text-xs font-normal">/{subscription.billingCycle === 'YEARLY' ? 'year' : 'month'}</span>
            </p>
            {(subscription.organizationCount ?? 1) > 1 && (
              <p className="text-xs text-(--color-muted-gray)">
                For {subscription.organizationCount} accounts
              </p>
            )}
            {subscription.coupon && (
              <p className="text-xs text-(--color-muted-gray)">
                {subscription.coupon.discountDuration === 'EVERY_PAYMENT'
                  ? `Includes coupon ${subscription.coupon.code}: ${Number(subscription.coupon.discountPercent)}% off ${
                      subscription.coupon.discountScope === 'PLAN_ONLY' ? 'the plan' : 'the whole payment'
                    }`
                  : `Coupon ${subscription.coupon.code} discounted your first payment only`}
              </p>
            )}
          </div>
        </div>

        {/* Auto-Renewal Toggle */}
        <div className="px-4 pb-4">
          <div className="flex items-center justify-between p-3 bg-[#0D1117] rounded-lg border border-gray-800">
            <div className="flex items-center gap-2.5">
              <RefreshCw className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-sm text-white font-medium">Auto-Renewal</p>
                <p className="text-xs text-(--color-muted-gray)">
                  {subscription.autoRenew 
                    ? 'Your subscription will automatically renew'
                    : 'Your subscription will not auto-renew'
                  }
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowAutoRenewDialog(true)}
              disabled={togglingAutoRenew}
              className="text-orange-400 hover:text-orange-300 transition-colors disabled:opacity-50"
              aria-label="Toggle auto-renewal"
            >
              {subscription.autoRenew
                ? <ToggleRight className="w-8 h-8" />
                : <ToggleLeft className="w-8 h-8 text-gray-500" />
              }
            </button>
          </div>
        </div>

        {/* Lead Generation Add-on Toggle */}
        <div className="px-4 pb-4">
          <div className="flex items-center justify-between p-3 bg-[#0D1117] rounded-lg border border-gray-800">
            <div className="flex items-center gap-2.5">
              <Bot className="w-4 h-4 text-orange-400" />
              <div>
                <p className="text-sm text-white font-medium">
                  Lead Generation Add-on
                  {addonRecurringPrice > 0 && (
                    <span className="ml-2 text-xs text-(--color-muted-gray) font-normal">
                      +{formatCurrency(addonRecurringPrice, subscription.currency)}/{addonCycleLabel}
                    </span>
                  )}
                </p>
                <p className="text-xs text-(--color-muted-gray)">
                  {subscription.leadGenAddonEnabled
                    ? `AI-powered lead generation is active — billed ${subscription.billingCycle === 'YEARLY' ? 'yearly' : 'monthly'} with your plan`
                    : 'Enable AI-powered lead generation. Charged now for this period, then billed with your plan.'}
                </p>
              </div>
            </div>
            <button
              onClick={() =>
                subscription.leadGenAddonEnabled
                  ? setShowDisableAddonDialog(true)
                  : openEnableAddonDialog()
              }
              disabled={togglingLeadGen}
              className="text-orange-400 hover:text-orange-300 transition-colors disabled:opacity-50"
              aria-label="Toggle Lead Generation add-on"
            >
              {subscription.leadGenAddonEnabled
                ? <ToggleRight className="w-8 h-8" />
                : <ToggleLeft className="w-8 h-8 text-gray-500" />
              }
            </button>
          </div>
        </div>
      </motion.div>

      {/* Payment Method Summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-[#121723] rounded-xl border border-gray-800 p-4"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-heading font-semibold text-white">Payment Method</h3>
          <Link 
            to={ROUTES.CUSTOMER.SETTINGS_PAYMENT_METHODS}
            className="text-orange-400 hover:text-orange-300 text-xs font-medium inline-flex items-center gap-1"
          >
            Manage
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {subscription.defaultPaymentMethod ? (
          <div className="flex items-center gap-3 p-3 bg-[#0D1117] rounded-lg border border-gray-800">
            <div className="w-10 h-7 bg-linear-to-br from-gray-700 to-gray-800 rounded flex items-center justify-center">
              <CreditCard className="w-4 h-4 text-gray-400" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-white font-medium capitalize">
                {subscription.defaultPaymentMethod.brand || 'Card'} •••• {subscription.defaultPaymentMethod.last4}
              </p>
              <p className="text-xs text-(--color-muted-gray)">
                Expires {subscription.defaultPaymentMethod.expiryMonth}/{subscription.defaultPaymentMethod.expiryYear}
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <CreditCard className="w-8 h-8 text-gray-600 mx-auto mb-2" />
            <p className="text-sm text-(--color-muted-gray) mb-2">No payment method on file</p>
            <Link to={ROUTES.CUSTOMER.SETTINGS_PAYMENT_METHODS}>
              <Button variant="secondary" size="sm">
                Add Payment Method
              </Button>
            </Link>
          </div>
        )}
      </motion.div>

      {/* Recent Payments */}
      {subscription.payments && subscription.payments.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#121723] rounded-xl border border-gray-800 p-4"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-heading font-semibold text-white">Recent Payments</h3>
            <Link 
              to={ROUTES.CUSTOMER.SETTINGS_PAYMENT_HISTORY}
              className="text-orange-400 hover:text-orange-300 text-xs font-medium inline-flex items-center gap-1"
            >
              View All
              <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="space-y-2">
            {subscription.payments.slice(0, 3).map((payment) => (
              <div 
                key={payment.id}
                className="flex items-center justify-between p-3 bg-[#0D1117] rounded-lg border border-gray-800"
              >
                <div className="flex items-center gap-2.5">
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center ${
                    payment.status === 'SUCCEEDED' ? 'bg-emerald-500/15' : 'bg-red-500/15'
                  }`}>
                    {payment.status === 'SUCCEEDED' 
                      ? <CheckCircle className="w-4 h-4 text-emerald-400" />
                      : <XCircle className="w-4 h-4 text-red-400" />
                    }
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">
                      {formatCurrency(payment.amount, subscription.currency)}
                    </p>
                    <p className="text-xs text-(--color-muted-gray)">
                      {payment.paidAt ? formatDate(payment.paidAt, true) : formatDate(payment.createdAt, true)}
                    </p>
                  </div>
                </div>
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                  payment.status === 'SUCCEEDED'
                    ? 'bg-emerald-500/15 text-emerald-400'
                    : 'bg-red-500/15 text-red-400'
                }`}>
                  {payment.status}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Account Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-[#121723] rounded-xl border border-gray-800 p-4"
      >
        <h3 className="text-sm font-heading font-semibold text-white mb-3">Account Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
            <p className="text-xs text-(--color-muted-gray) mb-0.5">Account Owner</p>
            <p className="text-sm text-white font-medium">{user?.firstName} {user?.lastName}</p>
          </div>
          <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
            <p className="text-xs text-(--color-muted-gray) mb-0.5">Email</p>
            <p className="text-sm text-white font-medium">{user?.email}</p>
          </div>
          <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
            <p className="text-xs text-(--color-muted-gray) mb-0.5">Organization</p>
            <p className="text-sm text-white font-medium">{user?.organization?.name || 'N/A'}</p>
          </div>
          <div className="p-3 bg-[#0D1117] rounded-lg border border-gray-800">
            <p className="text-xs text-(--color-muted-gray) mb-0.5">Member Since</p>
            <p className="text-sm text-white font-medium">
              {subscription.currentPeriodStart ? formatDate(subscription.currentPeriodStart) : 'N/A'}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Resume Subscription Success Message */}
      <AnimatePresence>
        {resumeSuccess && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4"
          >
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-emerald-400 mb-1">Subscription Resumed!</h3>
                <p className="text-xs text-emerald-400/80">
                  Welcome back! Your subscription has been successfully resumed. All features are now active.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cancel Subscription Success Message */}
      <AnimatePresence>
        {cancelSuccess && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-yellow-400 mb-1">Subscription Cancelled</h3>
                <p className="text-xs text-yellow-400/80">
                  Your subscription has been cancelled. You will lose access to all features at the end of your current billing period.
                  Redirecting to dashboard...
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cancel Subscription Section */}
      {subscription.status !== 'CANCELLED' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-[#121723] rounded-xl border border-red-900/30 p-4"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-sm font-heading font-semibold text-white mb-1">Cancel Subscription</h3>
                <p className="text-xs text-(--color-muted-gray) max-w-md">
                  Cancelling your subscription will remove access to all premium features including messaging, 
                  campaigns, analytics, and WhatsApp integration at the end of your billing period.
                </p>
              </div>
            </div>
            <Button 
              variant="danger" 
              size="sm"
              onClick={() => setShowCancelDialog(true)}
              className="shrink-0"
            >
              Cancel Subscription
            </Button>
          </div>
        </motion.div>
      )}

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onConfirm={handleCancelSubscription}
        variant="danger"
        title="Cancel Your Subscription?"
        description={`Are you sure you want to cancel your ${subscription.plan.name} subscription? 

This action will:
• Remove access to all messaging features
• Disable your WhatsApp integration
• Delete scheduled campaigns
• Remove access to analytics and reports

Your subscription will remain active until ${formatDate(subscription.currentPeriodEnd)}, after which you will lose all access.

This action cannot be undone.`}
        confirmText="Yes, Cancel Subscription"
        cancelText="Keep My Subscription"
        isLoading={cancelling}
      />

      {/* Auto-Renewal Toggle Confirmation */}
      <ConfirmDialog
        isOpen={showAutoRenewDialog}
        onClose={() => setShowAutoRenewDialog(false)}
        onConfirm={handleToggleAutoRenew}
        variant={subscription.autoRenew ? 'warning' : 'default'}
        title={subscription.autoRenew ? 'Turn Off Auto-Renewal?' : 'Turn On Auto-Renewal?'}
        description={
          subscription.autoRenew
            ? `Your ${subscription.plan.name} subscription will no longer renew automatically. It will stay active until ${formatDate(subscription.currentPeriodEnd)}, after which it will expire and you'll lose access unless you resubscribe.`
            : `Your ${subscription.plan.name} subscription will renew automatically on ${subscription.nextBillingDate ? formatDate(subscription.nextBillingDate) : 'your next billing date'} using your saved payment method.`
        }
        confirmText={subscription.autoRenew ? 'Turn Off Auto-Renewal' : 'Turn On Auto-Renewal'}
        cancelText="Cancel"
        isLoading={togglingAutoRenew}
      />

      {/* Disable Lead Gen Add-on Confirmation */}
      <ConfirmDialog
        isOpen={showDisableAddonDialog}
        onClose={() => setShowDisableAddonDialog(false)}
        onConfirm={() => handleToggleLeadGenAddon(false)}
        variant="warning"
        title="Disable Lead Generation Add-on?"
        description={subscription.coupon?.discountDuration === 'EVERY_PAYMENT'
          ? "The Lead Generation section will be removed and the add-on will no longer be included from your next renewal. Your recurring coupon will still apply to the plan. You've already paid for the current period — there's no refund, and you can re-enable it anytime."
          : `The Lead Generation section will be removed and your recurring payment will drop back to ${formatCurrency(subscription.planAmount, subscription.currency)}/${subscription.billingCycle === 'YEARLY' ? 'year' : 'month'} from the next renewal. You've already paid for the current period — there's no refund, and you can re-enable it anytime.`}
        confirmText="Disable Add-on"
        cancelText="Keep It"
        isLoading={togglingLeadGen}
      />

      {/* Enable Lead Gen Add-on Modal (choose card, charges immediately) */}
      <Modal
        isOpen={showEnableAddonDialog}
        onClose={() => !togglingLeadGen && setShowEnableAddonDialog(false)}
        title="Enable Lead Generation Add-on"
        subtitle={
          addonQuote
            ? `${addonAlreadyPaid ? 'Nothing to pay now' : `${formatCurrency(addonChargeNow, subscription.currency)} now`}, then ${
                // A recurring coupon also discounts renewals: show the exact next renewal instead of the list add-on price
                (addonQuote.coupon || subscription.coupon) && addonQuote.nextRenewalAmount !== undefined
                  ? `${formatCurrency(addonQuote.nextRenewalAmount, subscription.currency)}/${addonCycleLabel} in total with your plan`
                  : `${formatCurrency(addonRecurringPrice, subscription.currency)}/${addonCycleLabel} with your plan`
              }`
            : undefined
        }
        size="md"
        contentClassName="px-6 py-5"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-2.5 p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
            <Bot className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
            <p className="text-xs text-(--color-soft-gray)">
              {addonAlreadyPaid ? (
                <>
                  The add-on is already paid for this billing period, so there's nothing to pay now.
                  After that, it's added to your recurring plan payment. Turn it off anytime.
                </>
              ) : (
                <>
                  You'll be charged{' '}
                  <span className="text-white font-medium">{formatCurrency(addonChargeNow, subscription.currency)}</span> now
                  {addonQuote && addonQuote.monthsCharged > 1
                    ? ` for the ${addonQuote.monthsCharged} months left in your current period`
                    : ' for the current month'}
                  . After that, it's added to your recurring plan payment. Turn it off anytime.
                </>
              )}
            </p>
          </div>

          {!addonAlreadyPaid && <>
            <CouponField
              value={addonCouponInput}
              onChange={setAddonCouponInput}
              onApply={setAddonCouponCode}
              appliedCode={addonCouponCode}
              coupon={addonQuote?.coupon ?? subscription.coupon}
              error={couponError}
              disabled={togglingLeadGen}
            />
            {!!addonQuote?.discountAmount && (
              <p className="text-xs text-(--color-success)">
                Discount ({addonQuote.coupon?.code}): −{formatCurrency(addonQuote.discountAmount, subscription.currency)}
              </p>
            )}
          </>}
          {paymentMethods.length > 0 ? (
            <div>
              <p className="text-xs font-medium text-(--color-muted-gray) uppercase tracking-wider mb-2">
                Charge to
              </p>
              <div className="space-y-2">
                {paymentMethods.map((pm) => {
                  const selected = selectedAddonPmId === pm.id;
                  return (
                    <button
                      key={pm.id}
                      type="button"
                      onClick={() => setSelectedAddonPmId(pm.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                        selected
                          ? 'border-orange-400/60 bg-orange-500/10'
                          : 'border-gray-800 bg-[#0D1117] hover:border-gray-700'
                      }`}
                    >
                      <span
                        className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
                          selected ? 'border-orange-400' : 'border-gray-600'
                        }`}
                      >
                        {selected && <span className="w-2 h-2 rounded-full bg-orange-400" />}
                      </span>
                      <div className="w-9 h-6 bg-linear-to-br from-gray-700 to-gray-800 rounded flex items-center justify-center shrink-0">
                        <CreditCard className="w-4 h-4 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white font-medium capitalize">
                          {pm.brand || 'Card'} •••• {pm.last4}
                          {pm.isDefault && (
                            <span className="ml-2 text-[10px] uppercase tracking-wide text-orange-400 font-semibold">
                              Default
                            </span>
                          )}
                        </p>
                        {pm.expiryMonth && pm.expiryYear && (
                          <p className="text-xs text-(--color-muted-gray)">
                            Expires {pm.expiryMonth}/{pm.expiryYear}
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              <Link
                to={ROUTES.CUSTOMER.SETTINGS_PAYMENT_METHODS}
                className="inline-flex items-center gap-1 mt-2 text-xs text-orange-400 hover:text-orange-300"
              >
                Manage payment methods
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ) : (
            <div className="p-4 rounded-lg bg-[#0D1117] border border-gray-800 text-center">
              <p className="text-sm text-(--color-soft-gray) mb-3">
                You don't have a saved payment method yet.
              </p>
              <Link
                to={ROUTES.CUSTOMER.SETTINGS_PAYMENT_METHODS}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-400 hover:text-orange-300"
              >
                <CreditCard className="w-4 h-4" />
                Add a payment method
              </Link>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-red-500/10 border border-red-500/30">
              <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
              <p className="text-xs text-red-300">{error}</p>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-1">
            <Button
              variant="secondary"
              onClick={() => setShowEnableAddonDialog(false)}
              disabled={togglingLeadGen}
            >
              Not Now
            </Button>
            <Button
              variant="primary"
              onClick={() => handleToggleLeadGenAddon(true)}
              disabled={togglingLeadGen || !addonQuote || addonQuotedCode !== addonCouponCode || (!addonAlreadyPaid && !selectedAddonPmId)}
              isLoading={togglingLeadGen}
            >
              {addonAlreadyPaid
                ? 'Enable Add-on'
                : `Pay ${formatCurrency(addonChargeNow, subscription.currency)} & Enable`}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BillingPage;

